import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Pencil, Save, X } from "lucide-react";
import { DENOMS, money, editClosingRegisters } from "../../../lib/closing";

const Cell = ({ value, onChange, testid, step = "1" }) => (
  <input type="number" min="0" step={step} value={value} onChange={(e) => onChange(e.target.value)}
    className="input-swiss w-16 px-1 py-0.5 rounded-sm text-[12px] font-mono text-right" data-testid={testid} />
);

const toDraft = (regs) => Object.fromEntries(regs.map((r) => [r.register, { counts: { ...r.counts }, checks: r.checks_total, sale: r.total_sale }]));

export const RegistersTable = ({ closing, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(() => toDraft(closing.registers));
  const [busy, setBusy] = useState(false);
  const set = (reg, patch) => setDraft({ ...draft, [reg]: { ...draft[reg], ...patch } });

  const save = async () => {
    setBusy(true);
    try {
      const registers = Object.entries(draft).map(([register, d]) => ({
        register,
        counts: Object.fromEntries(DENOMS.map((x) => [x.key, parseInt(d.counts[x.key], 10) || 0])),
        checks_total: Number(d.checks) || 0,
        total_sale: Number(d.sale) || 0,
      }));
      await editClosingRegisters(closing.id, registers);
      toast.success("Register counts updated");
      setEditing(false);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Register counts{closing.edited_by ? ` · edited by ${closing.edited_by}` : ""}</span>
        {editing ? (
          <div className="flex gap-2">
            <button onClick={() => { setDraft(toDraft(closing.registers)); setEditing(false); }} className="text-[11px] px-2 py-1 rounded-sm border border-[color:var(--ups-border)] flex items-center gap-1" data-testid="closing-registers-cancel"><X size={12} /> Cancel</button>
            <button onClick={save} disabled={busy} className="btn-brutal text-[11px] px-2 py-1 rounded-sm font-bold flex items-center gap-1 disabled:opacity-50" data-testid="closing-registers-save">
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save counts
            </button>
          </div>
        ) : (
          <button onClick={() => { setDraft(toDraft(closing.registers)); setEditing(true); }} className="text-[11px] px-2 py-1 rounded-sm border border-[color:var(--ups-border)] flex items-center gap-1 hover:bg-[color:var(--ups-muted)]" data-testid="closing-registers-edit"><Pencil size={12} /> Edit counts</button>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[12px] font-mono" data-testid="closing-detail-registers">
          <thead className="text-[10px] uppercase tracking-[0.12em] text-[color:var(--ups-subtext)]">
            <tr><th className="text-left py-1">POS</th>{DENOMS.map((d) => <th key={d.key} className="text-right px-1">{d.label.replace(" bills", "")}</th>)}
              <th className="text-right px-1">Counted</th><th className="text-right px-1">Deposit</th><th className="text-right px-1">Checks</th><th className="text-right px-1">Sale</th></tr>
          </thead>
          <tbody>
            {closing.registers.map((r) => (
              <tr key={r.register} className="border-t border-[color:var(--ups-border)]">
                <td className="py-1.5 font-bold">{r.register}</td>
                {DENOMS.map((d) => (
                  <td key={d.key} className="text-right px-1">
                    {editing ? <Cell value={draft[r.register].counts[d.key] ?? 0} onChange={(v) => set(r.register, { counts: { ...draft[r.register].counts, [d.key]: v } })} testid={`closing-edit-${r.register}-${d.key}`} /> : r.counts[d.key] || 0}
                  </td>
                ))}
                <td className="text-right px-1">{money(r.cash_total)}</td><td className="text-right px-1">{money(r.cash_deposit)}</td>
                <td className="text-right px-1">{editing ? <Cell step="0.01" value={draft[r.register].checks} onChange={(v) => set(r.register, { checks: v })} testid={`closing-edit-${r.register}-checks`} /> : money(r.checks_total)}</td>
                <td className="text-right px-1">{editing ? <Cell step="0.01" value={draft[r.register].sale} onChange={(v) => set(r.register, { sale: v })} testid={`closing-edit-${r.register}-sale`} /> : money(r.total_sale)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
